/**
 * WebPlacementService
 *
 * Manages WebPlacement entities stored in subcollection.
 *
 * Storage: projects/{projectId}/webPlacements/{id}
 *
 * Key Features:
 * - CRUD operations for web placements (single and A/B test types)
 * - Project-scoped alias uniqueness (unique within project)
 * - Status management (draft → published)
 * - A/B test support with variant references
 * - Dependency protection (cannot delete placement used in A/B test)
 *
 * Placement Types:
 * - single: Regular web placement with direct funnel configuration
 * - ab_test: A/B test that routes to existing published WebPlacements
 *
 * Difference from PlacementService (iOS):
 * - Simpler schema (no targeting rules)
 * - Project-scoped alias uniqueness
 * - A/B test variants reference existing placements (not embedded configs)
 *
 * @see /modules/webplacements/MODULE.md for full documentation
 */

import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  setDoc,
  updateDoc,
  query,
  orderBy,
  where,
  limit,
  FirestoreDataConverter,
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';
import type {
  WebPlacement,
  WebPlacementType,
  WebABVariant,
  WebABIteration,
  CreateWebPlacementInput,
  UpdateWebPlacementInput,
} from '@modules/webplacements/types/webplacements.types';
import {
  WEB_PLACEMENT_VALIDATION,
  WEB_AB_VALIDATION,
  isWebABVariantRole,
  isValidWebAlias,
  getWebFunnelUrl,
} from '@modules/webplacements/types/webplacements.types';
import { webPlacementConverter } from '../converters/webPlacementConverter';
import { v4 as uuidv4 } from 'uuid';
import { buildWebABPublicationRecord } from '@modules/shared-types';
import { readPaddleCheckoutPolicy } from '@modules/paddle-connect/utils/paddleCheckoutPolicy';

export type WebABPublicationErrorCode =
  | 'ab_iteration_publish_conflict'
  | 'ab_iteration_snapshot_conflict';

/** Typed publication failure surfaced by the deploy service without leaking Firebase details. */
export class WebABPublicationError extends Error {
  readonly code: WebABPublicationErrorCode;

  constructor(code: WebABPublicationErrorCode, message: string) {
    super(message);
    this.name = 'WebABPublicationError';
    this.code = code;
  }
}

type ExpectedWebABCurrentIteration = Pick<WebABIteration, 'id' | 'routingHash'> | null;

// publishedAt/publishedBy are intentionally NOT part of the immutable identity:
// a retry after a failed publish rebuilds the same epoch with a fresh timestamp
// and must verify against the stored snapshot (first write wins), not conflict.
const immutableIterationProjection = (iteration: WebABIteration) => ({
  id: iteration.id,
  sequence: iteration.sequence,
  name: iteration.name,
  description: iteration.description || null,
  tags: iteration.tags || [],
  changeReason: iteration.changeReason || null,
  abSalt: iteration.abSalt,
  routingHash: iteration.routingHash,
  allocationMode: iteration.allocationMode
    || (iteration.variants.filter(variant => variant.weight > 0).length >= 2
      ? 'experiment'
      : 'delivery_only'),
  variants: iteration.variants.map(variant => ({
    id: variant.id,
    name: variant.name,
    description: variant.description || null,
    role: variant.role,
    tags: variant.tags || [],
    weight: variant.weight,
    webPlacementId: variant.webPlacementId,
    webPlacementAlias: variant.webPlacementAlias,
    webPlacementName: variant.webPlacementName,
  })),
});

const hasSameImmutableIteration = (
  stored: WebABIteration,
  candidate: WebABIteration
): boolean => JSON.stringify(immutableIterationProjection(stored))
  === JSON.stringify(immutableIterationProjection(candidate));

/**
 * WebPlacementService
 *
 * Type-safe service for managing Web Placements stored in subcollection.
 * Supports both single (funnel) and A/B test placement types.
 *
 * Usage:
 * ```typescript
 * const service = new WebPlacementService();
 *
 * // Create single web placement
 * const single = await service.create(projectId, {
 *   name: 'Summer Promo',
 *   alias: 'summer-promo',
 *   config: { funnelId: 'onb-1', versionId: 'v1.0' },
 *   createdBy: 'user@example.com'
 * });
 *
 * // Create A/B test placement
 * const abTest = await service.createABTest(projectId, {
 *   name: 'Summer A/B',
 *   alias: 'summer-ab',
 *   variants: [...],
 *   createdBy: 'user@example.com'
 * });
 *
 * // List published single placements (for variant picker)
 * const published = await service.listPublishedSingle(projectId);
 *
 * // Check alias uniqueness within project
 * const isUnique = await service.isAliasUnique(projectId, 'summer-promo');
 * ```
 */
export class WebPlacementService {
  private readonly collectionName = 'webPlacements';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<WebPlacement> = webPlacementConverter;

  // ==========================================================================
  // VALIDATION METHODS
  // ==========================================================================

  /**
   * Validate web placement name
   *
   * @param name - Name to validate
   * @throws Error if name is invalid
   */
  private validateName(name: string): void {
    if (!name?.trim()) {
      throw new Error('Web placement name is required');
    }

    const trimmed = name.trim();

    if (trimmed.length < WEB_PLACEMENT_VALIDATION.NAME_MIN_LENGTH) {
      throw new Error(
        `Web placement name must be at least ${WEB_PLACEMENT_VALIDATION.NAME_MIN_LENGTH} character`
      );
    }

    if (trimmed.length > WEB_PLACEMENT_VALIDATION.NAME_MAX_LENGTH) {
      throw new Error(
        `Web placement name must be at most ${WEB_PLACEMENT_VALIDATION.NAME_MAX_LENGTH} characters (got ${trimmed.length})`
      );
    }
  }

  /**
   * Validate web placement alias format
   *
   * @param alias - Alias to validate
   * @throws Error if alias format is invalid
   */
  private validateAliasFormat(alias: string): void {
    if (!alias) {
      throw new Error('Web placement alias is required');
    }

    if (!isValidWebAlias(alias)) {
      throw new Error(
        `Invalid alias format. Must be ${WEB_PLACEMENT_VALIDATION.ALIAS_MIN_LENGTH}-${WEB_PLACEMENT_VALIDATION.ALIAS_MAX_LENGTH} characters, ` +
        `lowercase letters, numbers and hyphens only. Reserved aliases: ${WEB_PLACEMENT_VALIDATION.RESERVED_ALIASES.join(', ')}`
      );
    }
  }

  /**
   * Validate config object (for single type placements)
   *
   * @param config - Config to validate
   * @throws Error if config is invalid
   */
  private validateConfig(config: WebPlacement['config']): void {
    if (!config) {
      throw new Error('Web placement config is required');
    }

    if (!config.funnelId?.trim()) {
      throw new Error('Config must have a funnelId');
    }

    if (!config.versionId?.trim()) {
      throw new Error('Config must have a versionId');
    }
  }

  /**
   * Validate A/B test variants
   *
   * @param variants - Variants array to validate
   * @throws Error if variants are invalid
   */
  private validateVariants(variants: WebABVariant[]): void {
    if (!variants || variants.length < WEB_AB_VALIDATION.VARIANT_MIN_COUNT) {
      throw new Error(
        `A/B test must have at least ${WEB_AB_VALIDATION.VARIANT_MIN_COUNT} variant(s)`
      );
    }

    if (variants.length > WEB_AB_VALIDATION.VARIANT_MAX_COUNT) {
      throw new Error(
        `A/B test cannot have more than ${WEB_AB_VALIDATION.VARIANT_MAX_COUNT} variants`
      );
    }

    // Check weights sum to 100
    const weightSum = variants.reduce((sum, v) => sum + v.weight, 0);
    if (weightSum !== WEB_AB_VALIDATION.WEIGHT_SUM) {
      throw new Error(
        `Variant weights must sum to ${WEB_AB_VALIDATION.WEIGHT_SUM} (currently: ${weightSum})`
      );
    }

    // Check at least one variant has weight > 0
    const hasActiveVariant = variants.some(v => v.weight > 0);
    if (!hasActiveVariant) {
      throw new Error('At least one variant must have weight > 0');
    }

    // Check all variants have required fields
    for (const variant of variants) {
      if (!variant.id) {
        throw new Error('All variants must have an ID');
      }
      if (!variant.name?.trim()) {
        throw new Error(`Variant "${variant.id}" must have a name`);
      }
      if (!variant.webPlacementId) {
        throw new Error(`Variant "${variant.id}" must reference a web placement`);
      }
      if (!variant.webPlacementAlias) {
        throw new Error(`Variant "${variant.id}" must have a web placement alias`);
      }
      if (variant.role !== undefined && !isWebABVariantRole(variant.role)) {
        throw new Error(`Variant "${variant.id}" has an invalid role`);
      }
    }
  }

  /**
   * Sanitize config object - trim strings, remove undefined values
   *
   * Ensures all config fields are properly saved to Firestore,
   * not just the core 5 fields (funnelId, versionId, etc.)
   *
   * @param config - Config to sanitize
   * @returns Sanitized config with all fields preserved
   */
  private sanitizeConfig(config: NonNullable<WebPlacement['config']>): NonNullable<WebPlacement['config']> {
    const sanitized: NonNullable<WebPlacement['config']> = {
      // Required fields (trimmed)
      funnelId: config.funnelId.trim(),
      versionId: config.versionId.trim(),
    };

    // Optional string fields (trimmed if present)
    if (config.entityName) sanitized.entityName = config.entityName.trim();
    if (config.funnelName) sanitized.funnelName = config.funnelName.trim();
    if (config.versionName) sanitized.versionName = config.versionName.trim();
    if (config.fallbackUrl) sanitized.fallbackUrl = config.fallbackUrl.trim();
    if (config.redirectUrl) sanitized.redirectUrl = config.redirectUrl.trim();

    // Boolean fields
    if (config.urlEncode !== undefined) sanitized.urlEncode = config.urlEncode;

    // Array fields (pass through if present and non-empty)
    if (config.urlParams?.length) sanitized.urlParams = config.urlParams;
    if (config.serverData?.length) sanitized.serverData = config.serverData;
    // Legacy field (predates variableInputs; removed from WebPlacementConfig type) — preserve pass-through for old docs
    const legacyScreenInputs = (config as { screenInputs?: unknown[] }).screenInputs;
    if (legacyScreenInputs?.length) (sanitized as { screenInputs?: unknown[] }).screenInputs = legacyScreenInputs;
    if (config.variableInputs?.length) sanitized.variableInputs = config.variableInputs;
    if (config.customVariables?.length) sanitized.customVariables = config.customVariables;
    if (config.parameters?.length) sanitized.parameters = config.parameters;
    if (config.redirectMappings?.length) sanitized.redirectMappings = config.redirectMappings;
    if (config.stripeMappings?.length) sanitized.stripeMappings = config.stripeMappings;
    if (config.variableBindings?.length) sanitized.variableBindings = config.variableBindings;
    if (config.entryScreenRules?.length) sanitized.entryScreenRules = config.entryScreenRules;

    // Object fields (pass through if present)
    if (config.conditionalRedirect) sanitized.conditionalRedirect = config.conditionalRedirect;
    if (config.dataCollection) sanitized.dataCollection = config.dataCollection;
    if (config.customAnalytics) sanitized.customAnalytics = config.customAnalytics;
    if (config.seo) sanitized.seo = config.seo;
    if (config.stripe) sanitized.stripe = config.stripe;
    if (config.consentManagement) sanitized.consentManagement = config.consentManagement;
    // Preserve explicit surface/navigation through save/read/publish; malformed
    // policies must fail rather than disappearing into the legacy overlay default.
    const paddleCheckout = readPaddleCheckoutPolicy(config.paddleCheckout);
    if (paddleCheckout !== undefined) sanitized.paddleCheckout = paddleCheckout;

    // Boolean fields - user data persistence (V2 only)
    if (config.saveUserData !== undefined) sanitized.saveUserData = config.saveUserData;

    // Boolean fields - payment method persistence for upsell (V2 only)
    if (config.rememberPaymentMethod !== undefined) sanitized.rememberPaymentMethod = config.rememberPaymentMethod;

    return sanitized;
  }

  // ==========================================================================
  // COLLECTION REFERENCES
  // ==========================================================================

  /**
   * Get collection reference for web placements
   */
  private getCollectionRef(projectId: string) {
    return collection(
      this.firestore,
      `projects/${projectId}/${this.collectionName}`
    ).withConverter(this.converter);
  }

  /**
   * Get document reference for a specific web placement
   */
  private getDocRef(projectId: string, webPlacementId: string) {
    return doc(
      this.firestore,
      `projects/${projectId}/${this.collectionName}`,
      webPlacementId
    ).withConverter(this.converter);
  }

  /**
   * Get document reference for an immutable A/B iteration snapshot.
   */
  private getABIterationDocRef(projectId: string, webPlacementId: string, iterationId: string) {
    return doc(
      this.firestore,
      `projects/${projectId}/${this.collectionName}/${webPlacementId}/abIterations`,
      iterationId
    );
  }

  private getABPublicationDocRef(projectId: string, webPlacementId: string, publicationId: string) {
    return doc(
      this.firestore,
      `projects/${projectId}/${this.collectionName}/${webPlacementId}/publications`,
      publicationId
    );
  }

  // ==========================================================================
  // ALIAS UNIQUENESS (PROJECT-SCOPED)
  // ==========================================================================

  /**
   * Check if alias is unique within a project
   *
   * Uses Firestore query with index on webPlacements.alias field.
   * Index configured in firestore.indexes.json (COLLECTION scope).
   *
   * @param projectId - Project ID to check within
   * @param alias - Alias to check
   * @param excludeId - Optional ID to exclude (for edit operations)
   * @returns true if alias is unique within the project
   */
  async isAliasUnique(projectId: string, alias: string, excludeId?: string): Promise<boolean> {
    try {
      // Query only within this project's webPlacements
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      );
      const q = query(
        collectionRef,
        where('alias', '==', alias),
        limit(1)
      );

      const querySnap = await getDocs(q);

      // If no documents found, alias is unique
      if (querySnap.empty) {
        return true;
      }

      // If excluding an ID (edit mode), check if found document is the one being edited
      if (excludeId) {
        const foundDoc = querySnap.docs[0];
        return foundDoc.id === excludeId;
      }

      // Alias is in use within this project
      return false;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.isAliasUnique');
      throw error;
    }
  }

  /**
   * Check if alias exists globally (for conflict detection across projects)
   *
   * @param alias - Alias to check
   * @returns true if alias exists in any project
   */
  async aliasExistsGlobally(alias: string): Promise<boolean> {
    try {
      const allWebPlacementsRef = collectionGroup(this.firestore, this.collectionName);
      const q = query(
        allWebPlacementsRef,
        where('alias', '==', alias),
        limit(1)
      );

      const querySnap = await getDocs(q);
      return !querySnap.empty;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.aliasExistsGlobally');
      throw error;
    }
  }

  // ==========================================================================
  // CRUD OPERATIONS
  // ==========================================================================

  /**
   * Create a new web placement (single or A/B test)
   *
   * @param projectId - Project ID
   * @param data - Web placement creation input
   * @returns Created web placement with auto-generated ID and timestamps
   * @throws Error if alias is not unique or validation fails
   */
  async create(projectId: string, data: CreateWebPlacementInput): Promise<WebPlacement> {
    try {
      const type: WebPlacementType = data.type || 'single';

      // Validate common fields
      this.validateName(data.name);
      this.validateAliasFormat(data.alias);

      // Type-specific validation
      if (type === 'single') {
        if (!data.config) {
          throw new Error('Config is required for single placement');
        }
        this.validateConfig(data.config);
      } else if (type === 'ab_test') {
        if (!data.variants || data.variants.length === 0) {
          throw new Error('Variants are required for A/B test');
        }
        this.validateVariants(data.variants);
      }

      // Check project-scoped alias uniqueness
      const isUnique = await this.isAliasUnique(projectId, data.alias);
      if (!isUnique) {
        throw new Error(`Alias "${data.alias}" is already in use in this project. Please choose a different alias.`);
      }

      const now = new Date().toISOString();

      // Build document data based on type
      const docData: Record<string, unknown> = {
        name: data.name.trim(),
        alias: data.alias.trim(),
        type,
        status: 'draft',
        createdAt: now,
        updatedAt: now,
        createdBy: data.createdBy,
      };

      // Add optional description
      if (data.description?.trim()) {
        docData.description = data.description.trim();
      }

      if (data.tags?.length) {
        docData.tags = data.tags.map(tag => tag.trim()).filter(Boolean);
      }

      // Source discriminator (v2.20.0): article-created placements are
      // excluded from list()/pickers — same isolation as article roots.
      if (data.sourceKind) {
        docData.sourceKind = data.sourceKind;
      }

      // Type-specific fields
      if (type === 'single' && data.config) {
        docData.config = this.sanitizeConfig(data.config);
      } else if (type === 'ab_test' && data.variants) {
        docData.variants = data.variants;
        docData.abSalt = uuidv4(); // Generate salt for A/B test
      }

      // Create document with auto-generated ID
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      );
      const docRef = await addDoc(collectionRef, docData);

      // Return created web placement
      const webPlacement: WebPlacement = {
        id: docRef.id,
        name: docData.name as string,
        alias: docData.alias as string,
        description: docData.description as string | undefined,
        tags: docData.tags as string[] | undefined,
        sourceKind: docData.sourceKind as WebPlacement['sourceKind'],
        type,
        config: type === 'single' ? (docData.config as WebPlacement['config']) : undefined,
        variants: type === 'ab_test' ? (docData.variants as WebABVariant[]) : undefined,
        abSalt: type === 'ab_test' ? (docData.abSalt as string) : undefined,
        status: 'draft',
        createdAt: docData.createdAt as string,
        updatedAt: docData.updatedAt as string,
        createdBy: docData.createdBy as string,
      };

      console.log(`[WebPlacementService] Created ${type} web placement: ${webPlacement.id} (alias: ${webPlacement.alias})`);

      return webPlacement;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.create');
      throw error;
    }
  }

  /**
   * Get web placement by ID
   *
   * @param projectId - Project ID
   * @param webPlacementId - Web placement ID
   * @returns WebPlacement or null if not found
   */
  async get(projectId: string, webPlacementId: string): Promise<WebPlacement | null> {
    try {
      const docRef = this.getDocRef(projectId, webPlacementId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return docSnap.data();
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.get');
      throw error;
    }
  }

  /**
   * Update web placement
   *
   * Note: type is immutable. Alias can only be changed while status is draft.
   *
   * @param projectId - Project ID
   * @param webPlacementId - Web placement ID
   * @param data - Partial data to update
   * @returns Updated web placement
   * @throws Error if web placement not found
   */
  async update(
    projectId: string,
    webPlacementId: string,
    data: UpdateWebPlacementInput
  ): Promise<WebPlacement> {
    try {
      // Get existing web placement
      const existing = await this.get(projectId, webPlacementId);

      if (!existing) {
        throw new Error(`Web placement "${webPlacementId}" not found`);
      }

      const type = existing.type || 'single';

      // Validate name if provided
      if (data.name !== undefined) {
        this.validateName(data.name);
      }

      const nextAlias = data.alias?.trim();
      const aliasChanged = nextAlias !== undefined && nextAlias !== existing.alias;
      if (nextAlias !== undefined) {
        this.validateAliasFormat(nextAlias);

        if (aliasChanged) {
          if (existing.status !== 'draft') {
            throw new Error('Alias can only be changed while the web placement is in draft status.');
          }

          const isUnique = await this.isAliasUnique(projectId, nextAlias, webPlacementId);
          if (!isUnique) {
            throw new Error(`Alias "${nextAlias}" is already in use in this project. Please choose a different alias.`);
          }
        }
      }

      // Type-specific validation
      if (type === 'single' && data.config !== undefined) {
        this.validateConfig(data.config);
      }
      if (type === 'ab_test' && data.variants !== undefined) {
        this.validateVariants(data.variants);
      }

      const now = new Date().toISOString();

      // Build update data
      const updateData: Record<string, unknown> = {
        updatedAt: now,
      };

      // Update name if provided
      if (data.name !== undefined) {
        updateData.name = data.name.trim();
      }

      if (aliasChanged) {
        updateData.alias = nextAlias;
      }

      // Update description if provided
      if (data.description !== undefined) {
        updateData.description = data.description.trim() || null;
      }

      if (data.tags !== undefined) {
        updateData.tags = data.tags.map(tag => tag.trim()).filter(Boolean);
      }

      // Update config if provided (single type)
      if (type === 'single' && data.config !== undefined) {
        updateData.config = this.sanitizeConfig(data.config);
        // Reset to draft when config changes
        updateData.status = 'draft';
      }

      // Update variants if provided (A/B test type)
      if (type === 'ab_test' && data.variants !== undefined) {
        updateData.variants = data.variants;
        // Reset to draft when variants change
        updateData.status = 'draft';
      }

      if (type === 'ab_test' && data.abSalt !== undefined) {
        updateData.abSalt = data.abSalt;
      }

      // Update document
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        webPlacementId
      );
      await updateDoc(docRef, updateData);

      // Return updated web placement
      const updated: WebPlacement = {
        ...existing,
        name: (updateData.name as string) || existing.name,
        alias: (updateData.alias as string) || existing.alias,
        description: updateData.description !== undefined
          ? (updateData.description as string | undefined)
          : existing.description,
        tags: updateData.tags !== undefined
          ? (updateData.tags as string[] | undefined)
          : existing.tags,
        config: type === 'single'
          ? ((updateData.config as WebPlacement['config']) || existing.config)
          : existing.config,
        variants: type === 'ab_test'
          ? ((updateData.variants as WebABVariant[]) || existing.variants)
          : existing.variants,
        abSalt: type === 'ab_test'
          ? ((updateData.abSalt as string) || existing.abSalt)
          : existing.abSalt,
        status: (updateData.status as WebPlacement['status']) || existing.status,
        updatedAt: now,
      };

      console.log(`[WebPlacementService] Updated ${type} web placement: ${webPlacementId}`);

      return updated;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.update');
      throw error;
    }
  }

  /**
   * Delete web placement
   *
   * @param projectId - Project ID
   * @param webPlacementId - Web placement ID
   * @throws Error if web placement not found
   */
  async delete(projectId: string, webPlacementId: string): Promise<void> {
    try {
      // Get existing web placement to verify it exists
      const existing = await this.get(projectId, webPlacementId);

      if (!existing) {
        throw new Error(`Web placement "${webPlacementId}" not found`);
      }

      if (existing.type === 'ab_test') {
        const iterationsRef = collection(
          this.firestore,
          `projects/${projectId}/${this.collectionName}/${webPlacementId}/abIterations`
        );
        const iterationsSnap = await getDocs(iterationsRef);
        await Promise.all(iterationsSnap.docs.map(iterationDoc => deleteDoc(iterationDoc.ref)));
      }

      // Delete document
      const docRef = this.getDocRef(projectId, webPlacementId);
      await deleteDoc(docRef);

      console.log(`[WebPlacementService] Deleted web placement: ${webPlacementId}`);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.delete');
      throw error;
    }
  }

  /**
   * List all web placements for a project
   *
   * @param projectId - Project ID
   * @returns Array of web placements ordered by updatedAt desc
   */
  async list(projectId: string): Promise<WebPlacement[]> {
    try {
      const collectionRef = this.getCollectionRef(projectId);
      const q = query(collectionRef, orderBy('updatedAt', 'desc'));
      const querySnap = await getDocs(q);

      // Article-created placements (sourceKind: 'article') ARE listed —
      // the Placements page badges them. Pickers that attach ONBOARDING
      // funnels filter them out at their own call sites (R6b).
      return querySnap.docs.map((docSnap) => docSnap.data());
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.list');
      throw error;
    }
  }

  /**
   * Direct lookup by project-scoped alias (v2.20.0). Unlike list(), does NOT
   * filter article-created placements — the article publish flow uses this
   * to reuse its own placement.
   */
  async getByAlias(projectId: string, alias: string): Promise<WebPlacement | null> {
    try {
      const collectionRef = this.getCollectionRef(projectId);
      const q = query(collectionRef, where('alias', '==', alias), limit(1));
      const querySnap = await getDocs(q);
      return querySnap.empty ? null : querySnap.docs[0].data();
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.getByAlias');
      throw error;
    }
  }

  // ==========================================================================
  // CLONE
  // ==========================================================================

  /**
   * Generate a unique copy alias within the project.
   *
   * Pattern: {base}-copy, {base}-copy-2, {base}-copy-3, ...
   * Truncates base if result exceeds ALIAS_MAX_LENGTH (50 chars).
   *
   * @param projectId - Project ID
   * @param sourceAlias - Original alias to base the copy on
   * @returns Unique alias string
   * @throws Error if no unique alias found after 99 attempts
   */
  private async generateCopyAlias(projectId: string, sourceAlias: string): Promise<string> {
    const maxLen = WEB_PLACEMENT_VALIDATION.ALIAS_MAX_LENGTH;

    for (let i = 1; i <= 99; i++) {
      const suffix = i === 1 ? '-copy' : `-copy-${i}`;
      const maxBaseLen = maxLen - suffix.length;
      const base = sourceAlias.length > maxBaseLen
        ? sourceAlias.slice(0, maxBaseLen)
        : sourceAlias;
      const candidate = `${base}${suffix}`;

      const isUnique = await this.isAliasUnique(projectId, candidate);
      if (isUnique) {
        return candidate;
      }
    }

    throw new Error(`Could not generate a unique alias for "${sourceAlias}" after 99 attempts`);
  }

  /**
   * Clone a web placement with cleared onboarding-dependent fields.
   *
   * Creates a new draft placement that preserves onboarding-independent settings
   * (URL params, server data, SEO, analytics, redirect config, etc.) while clearing
   * fields that reference a specific onboarding (funnelId, variableInputs, etc.).
   *
   * @param projectId - Project ID
   * @param sourceId - Source web placement ID to clone
   * @param userId - User email performing the clone
   * @returns Newly created web placement in draft status
   * @throws Error if source not found or alias generation fails
   */
  async clone(projectId: string, sourceId: string, userId: string): Promise<WebPlacement> {
    try {
      const source = await this.get(projectId, sourceId);
      if (!source) {
        throw new Error(`Web placement "${sourceId}" not found`);
      }

      const type: WebPlacementType = source.type || 'single';
      const now = new Date().toISOString();
      const alias = await this.generateCopyAlias(projectId, source.alias);

      // Build document data
      const docData: Record<string, unknown> = {
        name: `${source.name} (Copy)`,
        alias,
        type,
        status: 'draft',
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
      };

      if (source.description?.trim()) {
        docData.description = source.description;
      }

      if (source.tags?.length) {
        docData.tags = [...source.tags];
      }

      // Clone config for single type — clear onboarding-dependent fields
      if (type === 'single' && source.config) {
        const clonedConfig: Record<string, unknown> = JSON.parse(JSON.stringify(source.config));

        // Clear onboarding references
        clonedConfig.funnelId = '';
        clonedConfig.versionId = '';
        delete clonedConfig.entityName;
        delete clonedConfig.funnelName;
        delete clonedConfig.versionName;

        // Clear onboarding-dependent arrays
        delete clonedConfig.screenInputs;
        delete clonedConfig.variableInputs;
        delete clonedConfig.variableBindings;
        delete clonedConfig.entryScreenRules;

        // Clear stripe onboarding refs
        if (clonedConfig.stripe && typeof clonedConfig.stripe === 'object') {
          const stripe = clonedConfig.stripe as Record<string, unknown>;
          delete stripe.emailScreenId;
          delete stripe.emailVariableId;
          delete stripe.emailFieldPath;
          // Remove stripe object entirely if empty
          if (Object.keys(stripe).length === 0) {
            delete clonedConfig.stripe;
          }
        }

        // Filter mappings — remove entries referencing cleared sources
        const clearedSourceTypes = new Set(['screen', 'variable']);

        if (Array.isArray(clonedConfig.stripeMappings)) {
          clonedConfig.stripeMappings = (clonedConfig.stripeMappings as Array<{ sourceType: string }>)
            .filter(m => !clearedSourceTypes.has(m.sourceType));
          if ((clonedConfig.stripeMappings as unknown[]).length === 0) {
            delete clonedConfig.stripeMappings;
          }
        }

        if (Array.isArray(clonedConfig.redirectMappings)) {
          clonedConfig.redirectMappings = (clonedConfig.redirectMappings as Array<{ sourceType: string }>)
            .filter(m => !clearedSourceTypes.has(m.sourceType));
          if ((clonedConfig.redirectMappings as unknown[]).length === 0) {
            delete clonedConfig.redirectMappings;
          }
        }

        docData.config = clonedConfig;
      }

      // Clone variants for A/B test type
      if (type === 'ab_test' && source.variants) {
        docData.variants = JSON.parse(JSON.stringify(source.variants));
        docData.abSalt = uuidv4();
      }

      // Create document with auto-generated ID
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      );
      const docRef = await addDoc(collectionRef, docData);

      const webPlacement: WebPlacement = {
        id: docRef.id,
        name: docData.name as string,
        alias: docData.alias as string,
        description: docData.description as string | undefined,
        tags: docData.tags as string[] | undefined,
        type,
        config: type === 'single' ? (docData.config as WebPlacement['config']) : undefined,
        variants: type === 'ab_test' ? (docData.variants as WebABVariant[]) : undefined,
        abSalt: type === 'ab_test' ? (docData.abSalt as string) : undefined,
        status: 'draft',
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
      };

      console.log(`[WebPlacementService] Cloned ${type} web placement: ${source.id} → ${webPlacement.id} (alias: ${alias})`);

      return webPlacement;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.clone');
      throw error;
    }
  }

  /**
   * Clone a web placement without clearing funnel-specific references.
   *
   * This is the "Full Copy" path for operators who want an exact draft copy
   * of a working placement. It preserves config IDs by design: variable IDs,
   * enum option IDs stored in downstream conditions, screen IDs, mappings, and
   * Stripe email variable references must remain stable.
   */
  async cloneFull(projectId: string, sourceId: string, userId: string): Promise<WebPlacement> {
    try {
      const source = await this.get(projectId, sourceId);
      if (!source) {
        throw new Error(`Web placement "${sourceId}" not found`);
      }

      const type: WebPlacementType = source.type || 'single';
      const now = new Date().toISOString();
      const alias = await this.generateCopyAlias(projectId, source.alias);

      const docData: Record<string, unknown> = {
        name: `${source.name} (Full Copy)`,
        alias,
        type,
        status: 'draft',
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
      };

      if (source.description?.trim()) {
        docData.description = source.description;
      }

      if (source.tags?.length) {
        docData.tags = [...source.tags];
      }

      if (type === 'single' && source.config) {
        const clonedConfig: Record<string, unknown> = JSON.parse(JSON.stringify(source.config));
        delete clonedConfig.id;
        docData.config = clonedConfig;
      }

      if (type === 'ab_test' && source.variants) {
        docData.variants = JSON.parse(JSON.stringify(source.variants));
        docData.abSalt = uuidv4();
      }

      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      );
      const docRef = await addDoc(collectionRef, docData);

      const webPlacement: WebPlacement = {
        id: docRef.id,
        name: docData.name as string,
        alias: docData.alias as string,
        description: docData.description as string | undefined,
        tags: docData.tags as string[] | undefined,
        type,
        config: type === 'single' ? (docData.config as WebPlacement['config']) : undefined,
        variants: type === 'ab_test' ? (docData.variants as WebABVariant[]) : undefined,
        abSalt: type === 'ab_test' ? (docData.abSalt as string) : undefined,
        status: 'draft',
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
      };

      console.log(`[WebPlacementService] Full-cloned ${type} web placement: ${source.id} → ${webPlacement.id} (alias: ${alias})`);

      return webPlacement;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.cloneFull');
      throw error;
    }
  }

  // ==========================================================================
  // STATUS MANAGEMENT
  // ==========================================================================

  /**
   * Mark web placement as published
   *
   * Called after successful deployment to update status and record publish info.
   *
   * @param projectId - Project ID
   * @param webPlacementId - Web placement ID
   * @param userId - User email who published
   * @param webUrl - Optional explicit URL (otherwise generated from alias)
   * @param webShellVersion - Optional web-shell version baked at publish time
   * @returns Updated web placement
   */
  async markPublished(
    projectId: string,
    webPlacementId: string,
    userId: string,
    webUrl?: string,
    webShellVersion?: string
  ): Promise<WebPlacement> {
    try {
      // Get existing web placement
      const existing = await this.get(projectId, webPlacementId);

      if (!existing) {
        throw new Error(`Web placement "${webPlacementId}" not found`);
      }

      const now = new Date().toISOString();
      const url = webUrl || getWebFunnelUrl(existing.alias);

      // Update status to published
      const updateData: Record<string, unknown> = {
        status: 'published',
        publishedAt: now,
        publishedBy: userId,
        publishedUrl: url,
        updatedAt: now,
      };

      if (webShellVersion) {
        updateData.webShellVersion = webShellVersion;
      }

      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        webPlacementId
      );
      await updateDoc(docRef, updateData);

      // Return updated web placement
      const updated: WebPlacement = {
        ...existing,
        status: 'published',
        publishedAt: now,
        publishedBy: userId,
        publishedUrl: url,
        updatedAt: now,
        ...(webShellVersion ? { webShellVersion } : {}),
      };

      console.log(`[WebPlacementService] Marked as published: ${webPlacementId} (URL: ${url}, wsVersion: ${webShellVersion || 'n/a'})`);

      return updated;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.markPublished');
      throw error;
    }
  }

  /**
   * Persist (or verify) an immutable A/B iteration before the live routing
   * alias can reference it. This deliberately does not move the parent
   * pointer or supersede the current epoch: it is the metadata-first half of
   * cross-system publication. An upload failure may leave an unreferenced
   * immutable snapshot, which is safe; serving traffic must never reference
   * an epoch that the decision layer cannot resolve.
   */
  async prepareABTestIteration(
    projectId: string,
    webPlacementId: string,
    iteration: WebABIteration,
    expectedCurrentIteration: ExpectedWebABCurrentIteration
  ): Promise<void> {
    try {
      const iterationToStore: WebABIteration = {
        ...iteration,
        status: 'published',
      };
      const parentRef = this.getDocRef(projectId, webPlacementId);
      const iterationRef = this.getABIterationDocRef(
        projectId,
        webPlacementId,
        iterationToStore.id
      );

      await CoreFirebaseService.getInstance().runTransaction(async transaction => {
        const parentSnapshot = await transaction.get(parentRef);
        if (!parentSnapshot.exists()) {
          throw new Error(`Web placement "${webPlacementId}" not found`);
        }
        const existing = parentSnapshot.data();
        if (existing.type !== 'ab_test') {
          throw new Error(`Web placement "${webPlacementId}" is not an A/B test`);
        }

        const current = existing.abCurrentIteration;
        const expectedMatches = expectedCurrentIteration === null
          ? !current
          : current?.id === expectedCurrentIteration.id
            && current.routingHash === expectedCurrentIteration.routingHash;
        const alreadyActivated = current?.id === iterationToStore.id
          && current.routingHash === iterationToStore.routingHash;
        if (!expectedMatches && !alreadyActivated) {
          throw new WebABPublicationError(
            'ab_iteration_publish_conflict',
            'The A/B allocation changed while this publication was in progress. Republish the latest state.'
          );
        }
        if (
          current
          && current.id !== iterationToStore.id
          && iterationToStore.sequence <= current.sequence
        ) {
          throw new WebABPublicationError(
            'ab_iteration_publish_conflict',
            'The A/B iteration sequence is stale. Republish the latest state.'
          );
        }

        const iterationSnapshot = await transaction.get(iterationRef);
        if (iterationSnapshot.exists()) {
          const stored = iterationSnapshot.data() as WebABIteration;
          if (!hasSameImmutableIteration(stored, iterationToStore)) {
            throw new WebABPublicationError(
              'ab_iteration_snapshot_conflict',
              `Immutable A/B iteration "${iterationToStore.id}" already exists with different content.`
            );
          }
          return;
        }
        transaction.set(iterationRef, iterationToStore);
      });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.prepareABTestIteration');
      throw error;
    }
  }

  /**
   * Mark an A/B test as published and persist its current iteration snapshot.
   *
   * Snapshot persistence is create-or-verify: an existing immutable document is
   * never overwritten. The parent pointer is changed with compare-and-swap so a
   * stale concurrent publisher cannot activate an epoch built from old state.
   */
  async markABTestPublished(
    projectId: string,
    webPlacementId: string,
    userId: string,
    webUrl: string,
    iteration: WebABIteration,
    expectedCurrentIteration: ExpectedWebABCurrentIteration
  ): Promise<WebPlacement> {
    try {
      const now = new Date().toISOString();
      const iterationToStore: WebABIteration = {
        ...iteration,
        status: 'published',
        publishedAt: iteration.publishedAt || now,
        publishedBy: iteration.publishedBy || userId,
      };

      const parentRef = this.getDocRef(projectId, webPlacementId);
      const iterationRef = this.getABIterationDocRef(
        projectId,
        webPlacementId,
        iterationToStore.id
      );

      const updated = await CoreFirebaseService.getInstance().runTransaction(async transaction => {
        const parentSnapshot = await transaction.get(parentRef);
        if (!parentSnapshot.exists()) {
          throw new Error(`Web placement "${webPlacementId}" not found`);
        }

        const existing = parentSnapshot.data();
        if (existing.type !== 'ab_test') {
          throw new Error(`Web placement "${webPlacementId}" is not an A/B test`);
        }

        const current = existing.abCurrentIteration;
        const expectedMatches = expectedCurrentIteration === null
          ? !current
          : current?.id === expectedCurrentIteration.id
            && current.routingHash === expectedCurrentIteration.routingHash;
        const alreadyActivated = current?.id === iterationToStore.id
          && current.routingHash === iterationToStore.routingHash;

        if (!expectedMatches && !alreadyActivated) {
          throw new WebABPublicationError(
            'ab_iteration_publish_conflict',
            'The A/B allocation changed while this publication was in progress. Republish the latest state.'
          );
        }

        if (
          current
          && current.id !== iterationToStore.id
          && iterationToStore.sequence <= current.sequence
        ) {
          throw new WebABPublicationError(
            'ab_iteration_publish_conflict',
            'The A/B iteration sequence is stale. Republish the latest state.'
          );
        }

        const iterationSnapshot = await transaction.get(iterationRef);
        if (iterationSnapshot.exists()) {
          const stored = iterationSnapshot.data() as WebABIteration;
          if (!hasSameImmutableIteration(stored, iterationToStore)) {
            throw new WebABPublicationError(
              'ab_iteration_snapshot_conflict',
              `Immutable A/B iteration "${iterationToStore.id}" already exists with different content.`
            );
          }
        } else {
          transaction.set(iterationRef, iterationToStore);
        }

        if (current && current.id !== iterationToStore.id) {
          transaction.set(
            this.getABIterationDocRef(projectId, webPlacementId, current.id),
            {
              status: 'superseded',
              supersededAt: iterationToStore.publishedAt,
            },
            { merge: true }
          );
        }

        const abIterationSequence = Math.max(
          existing.abIterationSequence || 0,
          iterationToStore.sequence
        );
        const publishRevision = (Number.isInteger(existing.publishRevision)
          // Number.isInteger(undefined) === false, so the true branch is a number
          ? existing.publishRevision!
          : 0) + 1;
        const publicationId = `pub_${String(publishRevision).padStart(4, '0')}`;
        const publication = buildWebABPublicationRecord({
          id: publicationId,
          revision: publishRevision,
          iterationId: iterationToStore.id,
          routingHash: iterationToStore.routingHash,
          allocationMode: iterationToStore.allocationMode
            || (iterationToStore.variants.filter(variant => variant.weight > 0).length >= 2
              ? 'experiment'
              : 'delivery_only'),
          label: iterationToStore.name,
          description: iterationToStore.description ?? existing.description ?? null,
          parentTags: existing.tags,
          iterationTags: iterationToStore.tags,
          publishedAt: now,
          publishedBy: userId,
          gcsPath: `web-ab/${projectId}/${existing.alias}.json`,
          webUrl,
          variants: iterationToStore.variants,
        });
        const updateData = {
          status: 'published' as const,
          publishedAt: now,
          publishedBy: userId,
          publishedUrl: webUrl,
          updatedAt: now,
          abCurrentIteration: iterationToStore,
          abIterationSequence,
          publishRevision,
          latestPublicationId: publicationId,
          latestPublication: publication,
        };

        transaction.set(
          this.getABPublicationDocRef(projectId, webPlacementId, publicationId),
          publication
        );
        transaction.set(parentRef, updateData, { merge: true });

        return {
          ...existing,
          ...updateData,
        } as WebPlacement;
      });

      console.log(`[WebPlacementService] Marked A/B test as published: ${webPlacementId} (iteration: ${iterationToStore.id})`);

      return updated;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.markABTestPublished');
      throw error;
    }
  }

  /**
   * List historical A/B iterations for an A/B test.
   */
  async listABIterations(projectId: string, webPlacementId: string): Promise<WebABIteration[]> {
    try {
      const iterationsRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}/${webPlacementId}/abIterations`
      );
      const q = query(iterationsRef, orderBy('sequence', 'desc'));
      const querySnap = await getDocs(q);

      return querySnap.docs.map(docSnap => docSnap.data() as WebABIteration);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.listABIterations');
      throw error;
    }
  }

  // ==========================================================================
  // A/B TEST SPECIFIC METHODS
  // ==========================================================================

  /**
   * List published single (funnel) placements for variant picker
   *
   * @param projectId - Project ID
   * @returns Array of published single placements
   */
  async listPublishedSingle(projectId: string): Promise<WebPlacement[]> {
    try {
      const collectionRef = this.getCollectionRef(projectId);
      // Note: Firestore requires composite index for type + status query
      // Fallback: get all and filter in memory if index doesn't exist
      const q = query(collectionRef, orderBy('updatedAt', 'desc'));
      const querySnap = await getDocs(q);

      return querySnap.docs
        .map((docSnap) => docSnap.data())
        .filter((wp) => {
          const type = wp.type || 'single'; // Default to single for backward compat
          // A/B variants carry onboarding funnels — article-backed placements
          // are not eligible variant sources (R6b picker exclusion).
          return type === 'single' && wp.status === 'published' && wp.sourceKind !== 'article';
        });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.listPublishedSingle');
      throw error;
    }
  }

  /**
   * List placements by type
   *
   * @param projectId - Project ID
   * @param type - Placement type to filter by
   * @returns Array of placements of the specified type
   */
  async listByType(projectId: string, type: WebPlacementType): Promise<WebPlacement[]> {
    try {
      const collectionRef = this.getCollectionRef(projectId);
      const q = query(collectionRef, orderBy('updatedAt', 'desc'));
      const querySnap = await getDocs(q);

      return querySnap.docs
        .map((docSnap) => docSnap.data())
        .filter((wp) => {
          const wpType = wp.type || 'single'; // Default to single for backward compat
          return wpType === type;
        });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.listByType');
      throw error;
    }
  }

  /**
   * Check if a placement is used as a variant in any A/B test
   *
   * Used for dependency protection before deletion.
   *
   * @param projectId - Project ID
   * @param webPlacementId - Placement ID to check
   * @returns Array of A/B test names that reference this placement
   */
  async getABTestsUsingPlacement(projectId: string, webPlacementId: string): Promise<string[]> {
    try {
      const allPlacements = await this.list(projectId);

      const usingTests = allPlacements.filter((wp) => {
        if (wp.type !== 'ab_test' || !wp.variants) return false;
        return wp.variants.some((v: WebABVariant) => v.webPlacementId === webPlacementId);
      });

      return usingTests.map((t) => t.name);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.getABTestsUsingPlacement');
      throw error;
    }
  }

  /**
   * Validate that all A/B test variant references are still valid
   *
   * Checks that all referenced placements exist and are published.
   *
   * @param projectId - Project ID
   * @param placement - A/B test placement to validate
   * @returns Validation result with errors
   */
  async validateABTestReferences(
    projectId: string,
    placement: WebPlacement
  ): Promise<{ valid: boolean; errors: string[] }> {
    const errors: string[] = [];

    if (placement.type !== 'ab_test') {
      errors.push('Not an A/B test placement');
      return { valid: false, errors };
    }

    if (!placement.variants || placement.variants.length === 0) {
      errors.push('A/B test has no variants');
      return { valid: false, errors };
    }

    // Check each variant reference
    for (const variant of placement.variants) {
      const refPlacement = await this.get(projectId, variant.webPlacementId);

      if (!refPlacement) {
        errors.push(`Variant "${variant.name}" references deleted placement "${variant.webPlacementName}"`);
        continue;
      }

      if (refPlacement.status !== 'published') {
        errors.push(`Variant "${variant.name}" references unpublished placement "${refPlacement.name}"`);
      }

      // Check if alias changed (denormalized data stale)
      if (refPlacement.alias !== variant.webPlacementAlias) {
        errors.push(
          `Variant "${variant.name}" has stale alias (expected: "${refPlacement.alias}", got: "${variant.webPlacementAlias}")`
        );
      }
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Reset A/B test salt to redistribute users
   *
   * Generates a new salt for the A/B test, causing all users to be
   * redistributed to variants. Use with caution!
   *
   * @param projectId - Project ID
   * @param webPlacementId - A/B test placement ID
   * @returns Updated placement with new salt
   */
  async resetABSalt(projectId: string, webPlacementId: string): Promise<WebPlacement> {
    try {
      const existing = await this.get(projectId, webPlacementId);

      if (!existing) {
        throw new Error(`Web placement "${webPlacementId}" not found`);
      }

      if (existing.type !== 'ab_test') {
        throw new Error('Can only reset salt for A/B test placements');
      }

      const now = new Date().toISOString();
      const newSalt = uuidv4();

      const updateData = {
        abSalt: newSalt,
        updatedAt: now,
        status: 'draft', // Reset to draft as redistribution will happen
      };

      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        webPlacementId
      );
      await updateDoc(docRef, updateData);

      const updated: WebPlacement = {
        ...existing,
        abSalt: newSalt,
        status: 'draft',
        updatedAt: now,
      };

      console.log(`[WebPlacementService] Reset A/B salt for: ${webPlacementId}`);

      return updated;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.resetABSalt');
      throw error;
    }
  }

  // ==========================================================================
  // FOLDER OPERATIONS (for A/B tests only)
  // ==========================================================================

  /**
   * List A/B test placements in a specific folder
   *
   * @param projectId - Project ID
   * @param folderId - Folder ID to filter by
   * @returns Array of A/B test placements in the folder
   */
  async listABTestsByFolder(projectId: string, folderId: string): Promise<WebPlacement[]> {
    try {
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      ).withConverter(webPlacementConverter);

      const q = query(
        collectionRef,
        where('type', '==', 'ab_test'),
        where('folderId', '==', folderId),
        orderBy('updatedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => doc.data());
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.listABTestsByFolder');
      throw error;
    }
  }

  /**
   * List root-level A/B test placements (not in any folder)
   *
   * @param projectId - Project ID
   * @returns Array of A/B test placements at root level
   */
  async listRootABTests(projectId: string): Promise<WebPlacement[]> {
    try {
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      ).withConverter(webPlacementConverter);

      // Query for A/B tests where folderId is null or doesn't exist
      const q = query(
        collectionRef,
        where('type', '==', 'ab_test'),
        orderBy('updatedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);

      // Filter out items that have a folderId (since Firestore doesn't support != null directly)
      return querySnapshot.docs
        .map((doc) => doc.data())
        .filter((wp) => wp.folderId === null || wp.folderId === undefined);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.listRootABTests');
      throw error;
    }
  }

  /**
   * List all A/B test placements for a project
   *
   * @param projectId - Project ID
   * @returns Array of all A/B test placements
   */
  async listAllABTests(projectId: string): Promise<WebPlacement[]> {
    try {
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      ).withConverter(webPlacementConverter);

      const q = query(
        collectionRef,
        where('type', '==', 'ab_test'),
        orderBy('updatedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => doc.data());
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.listAllABTests');
      throw error;
    }
  }

  /**
   * Move A/B test to a folder (or to root)
   *
   * @param projectId - Project ID
   * @param webPlacementId - A/B test placement ID to move
   * @param folderId - Target folder ID, or null to move to root
   */
  async moveToFolder(projectId: string, webPlacementId: string, folderId: string | null): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        webPlacementId
      );

      await updateDoc(docRef, {
        folderId: folderId,
        updatedAt: new Date().toISOString(),
      });

      console.log(`[WebPlacementService] Moved A/B test ${webPlacementId} to folder: ${folderId ?? 'root'}`);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.moveToFolder');
      throw error;
    }
  }

  /**
   * Count A/B tests in a specific folder
   *
   * @param projectId - Project ID
   * @param folderId - Folder ID to count items in
   * @returns Number of A/B tests in the folder
   */
  async countABTestsInFolder(projectId: string, folderId: string): Promise<number> {
    try {
      const abTests = await this.listABTestsByFolder(projectId, folderId);
      return abTests.length;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebPlacementService.countABTestsInFolder');
      throw error;
    }
  }
}
